import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { PatientAttachment } from '../../../core/models/patient-attachment.model';
import { PatientAttachmentsService } from '../../../core/services/patient-attachments.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { UserTimingService } from '../../../core/services/user-timing.service';
import { PdfFileType } from '../../../core/enums/pdf-file-type.enum';
import { keyedPatientAttachmentStatusOptions } from '../../../core/options/patient-attachment-status.opts';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { environment } from '../../../../environments/environment';
import { NgClass, DatePipe } from '@angular/common';
import { ConsultNoteTitleComponent } from '../../components/consult-note-title/consult-note-title.component';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { ConsultNoteOfficeComponent } from '../../../shared/components/consult-note-office/consult-note-office.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { PdfFileViewerComponent } from '../../../shared/components/pdf-file-viewer/pdf-file-viewer.component';
import { PdfkitFileViewerComponent } from '../../../shared/components/pdfkit-file-viewer/pdfkit-file-viewer.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BlankValueDashComponent } from '../../../shared/components/blank-value-dash/blank-value-dash.component';
import { FaxStatusPipe } from '../../../shared/pipes/fax-status.pipe';
import { PatientAttachmentListingDisplayNamePipe } from 'app/shared/pipes/patient-attachment-listing-display-name.pipe';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';

@Component({
    selector: 'app-consult-note-related',
    templateUrl: './consult-note-related.component.html',
    styleUrls: ['./consult-note-related.component.scss'],
    imports: [ConsultNoteTitleComponent,
              RouterLink,
              PatientNameComponent,
              ConsultNoteOfficeComponent,
              NgClass,
              PrescriberNameComponent,
              PdfFileViewerComponent,
              PdfkitFileViewerComponent,
              MatProgressSpinner,
              BlankValueDashComponent,
              DatePipe,
              FaxStatusPipe,
              PatientAttachmentListingDisplayNamePipe,
              BlankValueDashPipe]
})
export class ConsultNoteRelatedComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private titleService = inject(Title);
  private authService = inject(AuthenticationService);
  private userTimingService = inject(UserTimingService);
  private userPresenceService = inject(UserPresenceService);
  private navigationService = inject(NavigationService);

  patientAttachmentId: string;
  patientAttachment: PatientAttachment;
  selectedAttachment: PatientAttachment;
  loading = true;
  selectedLoading = false;
  pdfFileType: PdfFileType = PdfFileType.patientAttachment;
  statusOptions = keyedPatientAttachmentStatusOptions;
  usePdfKitViewer: boolean;

  ngOnInit() {
    this.patientAttachmentId = this.route.snapshot.paramMap.get('id');
    this.loadPatientAttachments(this.patientAttachmentId);
    this.userPresenceService.subscribeAndJoin("consultNote", this.patientAttachmentId)
    this.usePdfKitViewer = environment.featureData?.usePdfKitViewer && this.authService.isCaptureAdminUser;
  }

  ngOnDestroy() {
    this.stopTimeTracking();
    this.userPresenceService.leaveAndUnsubscribe("consultNote", this.patientAttachmentId)
  }

  onDisplayDocument(id: number) {
    if (id === this.patientAttachment.listing.id) {
      this.selectedAttachment = this.patientAttachment
    } else {
      this.loadSelectedAttachment(id)
    }
  }

  private loadPatientAttachments(patientAttachmentId: string): void {
    this.loading = true;

    this.patientAttachmentsService.get(patientAttachmentId).subscribe(
      result => {
        this.patientAttachment = result;
        this.selectedAttachment = result;
        this.startTimeTracking(this.patientAttachment);
        this.setTitle();
        this.loading = false;
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.navigationService.notFound();
        } else {
          console.error(err);
          this.loading = false;
        }
      }
    );
  }

  private loadSelectedAttachment(id: number) {
    this.selectedLoading = true;
    this.selectedAttachment = null;

    this.patientAttachmentsService.get(id).subscribe(
      result => {
        this.selectedAttachment = result;
        this.selectedLoading = false;
      },
      (err: HttpErrorResponse) => {
        console.error(err);
        this.selectedLoading = false;
      }
    );
  }

  private setTitle() {
    const title = `R1 340B Recovery - ${this.patientAttachment.id} | Related Patient Documents`;
    this.titleService.setTitle(title);
  }

  private startTimeTracking(patientAttachment: PatientAttachment) {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.track({
        page: 'other_patient_consult_notes',
        recordId: patientAttachment.id,
        status: patientAttachment.status,
      });
    }
  }

  private stopTimeTracking() {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.stop();
    }
  }
}
